import { BoardState, Position } from "./BoardState";
import { ComputerDirector } from "../strategy/ComputerDirector";
import { Piece } from "./Piece";
import { CheckmateChecker } from "../strategy/CheckmateChecker";
import logger from "../utils/Logger";

class Game {
  private readonly boardState: BoardState;
  private computerDirector: ComputerDirector;
  private gameOver: boolean;
  private currentTurn: 'Player' | 'Computer';
  private readonly capturedComputerPieces: Piece[];
  private readonly capturedPlayerPieces: Piece[];
  private readonly gameOverCallback: (message: string) => void;
  private checkmateChecker: CheckmateChecker;
  private readonly setAlertMessageCallback: (message: string) => void;
  private readonly setAlertVisibleCallback: (visible: boolean) => void;
  private turnCount: number;

  constructor(boardState: BoardState,
              previousGame: Game | null = null,
              setAlertMessageCallback: (message: string) => void,
              setAlertVisibleCallback: (visible: boolean) => void,
              gameOverCallback: (message: string) => void = () => {},
              private readonly triggerComputerMoveAnimationCallback: (from: Position, to: Position) => Promise<void> = async () => {}) {
    // Initialize the game state, if we have previous game state, use it.
    this.boardState = boardState;
    this.computerDirector = previousGame ? previousGame.computerDirector : new ComputerDirector();
    this.setAlertMessageCallback = setAlertMessageCallback;
    this.setAlertVisibleCallback = setAlertVisibleCallback;
    this.gameOver = previousGame ? previousGame.gameOver : false;
    this.currentTurn = previousGame ? previousGame.currentTurn : 'Player';
    this.turnCount = previousGame ? previousGame.turnCount : 0;
    this.capturedComputerPieces = previousGame ? previousGame.capturedComputerPieces : [];
    this.capturedPlayerPieces = previousGame ? previousGame.capturedPlayerPieces : [];
    this.gameOverCallback = gameOverCallback;
    this.checkmateChecker = new CheckmateChecker(boardState);
  }

  // Handle the player's move
  public handlePlayerMove(from: Position, to: Position) {
    if (this.currentTurn !== 'Player' || this.gameOver) {
      return;
    }
    this.handleMove(from, to);

    this.checkForCheckAndCheckmate(); // Check for check and checkmate before switching the turn
    
    // Check for stalemate after player's move
    this.checkForStalemate();
    
    // Check for draw after player's move
    this.checkForDraw();

    this.switchTurn(); // Switch the turn to computer after handling the player move
  }

  // Handle the computer's move
  public async handleComputerMove(): Promise<void | { from: Position; to: Position }> {
    if (this.currentTurn !== 'Computer' || this.gameOver) {
      return;
    }

    // Log the current board state for debugging
    logger.debug("Current board state:", JSON.stringify(this.boardState.pieces.map(p => ({
      type: p.type,
      color: p.color,
      position: p.position
    }))));

    // Get the computer's strategy and legal moves
    const strategy = this.computerDirector.getStrategy(this.boardState);
    const legalMoves = strategy.getLegalMoves(null, this.boardState);
    
    if (legalMoves.length > 0) {
      const move = legalMoves[0];
      const piece = this.boardState.pieces.find(p => p.position.row === move.piece!.position.row && p.position.col === move.piece!.position.col);
      if (piece) {
        logger.debug(`Piece ${piece.type} is moving from (${piece.position.row}, ${piece.position.col}) to (${move.row}, ${move.col})`);

        // Trigger animation and wait for it to complete
        await this.triggerComputerMoveAnimationCallback(piece.position, { row: move.row, col: move.col });

        // Check if the move captures a piece
        const targetPiece = this.boardState.pieces.find(p => p.position.row === move.row && p.position.col === move.col);
        if (targetPiece && targetPiece.type === 'king') {
          this.handleMove(piece.position, move);
          this.gameOver = true;
          this.gameOverCallback('Computer captured the King!');
        } else {
          this.handleMove(piece.position, move);
          this.playBeepSound();
        }
        
        this.checkForCheckAndCheckmate(); // Check for check and checkmate before switching the turn
        
        // Check for stalemate after computer's move
        this.checkForStalemate();
        
        // Check for draw after computer's move
        this.checkForDraw();
        
        this.switchTurn(); // Switch the turn to player after handling the computer move
        return { from: piece.position, to: move };
      }
    } else {
      // Computer has no legal moves, game is stalemate.
      const msg = "No legal moves left, game is stalemate!";
      this.showAlert(msg);
      this.gameOver = true;
      this.gameOverCallback(msg);
    }

    this.checkForCheckAndCheckmate(); // Check for check and checkmate before switching the turn
    
    // Check for stalemate after computer's move
    this.checkForStalemate();
    
    // Check for draw after computer's move
    this.checkForDraw();
    
    this.switchTurn(); // Switch the turn to player after handling the computer move
  }

  // Handle the move logic for both player and computer
  private handleMove(from: Position, to: Position) {
    // Create a new array to hold the updated pieces
    const updatedPieces: Piece[] = [];

    // Iterate over each piece in the current board state
    for (const piece of this.boardState.pieces) {
      // Check if the piece is at the destination position and belongs to the opponent
      if (piece.position.row === to.row && piece.position.col === to.col
        && piece.color !== (this.currentTurn === 'Player' ? 'black' : 'white')) {
        // Play capture sound and update the list of captured pieces
        this.playCaptureSound();
        this.updateCapturedPieces(piece);
        // Show an alert that a piece has been captured
        this.showAlert(`${this.currentTurn} captured a ${piece.type}!`);
        // If the captured piece is the king, end the game
        if (piece.type === 'king') {
          this.gameOver = true;
          this.gameOverCallback(`${this.currentTurn} captured the King!`);
        }
        // Skip adding this piece to the updated pieces array (it is captured)
        continue;
      }

      // Check if the piece is the one being moved
      if (piece.position.row === from.row && piece.position.col === from.col) {
        // Update the position of the moved piece
        updatedPieces.push({ ...piece, position: to });
      } else {
        // Add the piece unchanged to the updated pieces array
        updatedPieces.push(piece);
      }
    }

    // Update the board state with the new pieces array
    this.boardState.pieces = updatedPieces;
  }

  private checkForCheckAndCheckmate() {
    // First check if the current player's move put opponent in check
    const opponentTurn = this.currentTurn === 'Player' ? 'Computer' : 'Player';
    const checkInfo = this.checkmateChecker.isInCheck(opponentTurn);
    
    if (checkInfo.inCheck) {
      this.showAlert(`${opponentTurn} is in check by ${checkInfo.piece?.type} at (${checkInfo.position?.row}, ${checkInfo.position?.col})!`);
      
      // If opponent is in check, check if they are also in checkmate
      if (this.checkmateChecker.isCheckmated(opponentTurn)) {
        this.gameOver = true;
        this.showAlert(`${opponentTurn} is checkmated!`);
        this.gameOverCallback(`${opponentTurn} is checkmated! ${this.currentTurn} wins!`);
      }
    }
    
    // Also check if the current player is in checkmate (when computer makes a move, check if player is checkmated)
    const currentPlayerCheckInfo = this.checkmateChecker.isInCheck(this.currentTurn);
    if (currentPlayerCheckInfo.inCheck) {
      // Only show this alert if we're not already showing a "player put opponent in check" message
      if (!checkInfo.inCheck) {
        this.showAlert(`${this.currentTurn} is in check by ${currentPlayerCheckInfo.piece?.type} at (${currentPlayerCheckInfo.position?.row}, ${currentPlayerCheckInfo.position?.col})!`);
      }
      
      // Check if current player is checkmated
      if (this.checkmateChecker.isCheckmated(this.currentTurn)) {
        this.gameOver = true;
        this.showAlert(`${this.currentTurn} is checkmated!`);
        this.gameOverCallback(`${this.currentTurn} is checkmated! ${opponentTurn} wins!`);
      }
    }
  }
  
  private checkForStalemate() {
    // Check if the player is in stalemate after the computer's move
    if (this.currentTurn === 'Computer' && !this.gameOver) {
      if (this.checkmateChecker.isStalemate('Player')) {
        const msg = "Player has no legal moves, game is stalemate!";
        this.showAlert(msg);
        this.gameOver = true;
        this.gameOverCallback(msg);
      }
    }
    
    // Check if the computer is in stalemate after the player's move
    if (this.currentTurn === 'Player' && !this.gameOver) {
      if (this.checkmateChecker.isStalemate('Computer')) {
        const msg = "Computer has no legal moves, game is stalemate!";
        this.showAlert(msg);
        this.gameOver = true;
        this.gameOverCallback(msg);
      }
    }
  }

  private checkForDraw() {
    if (!this.gameOver && this.checkmateChecker.isDraw()) {
      const msg = "Game is a draw - both players have only kings remaining!";
      this.showAlert(msg);
      this.gameOver = true;
      this.gameOverCallback(msg);
    }
  }

  // Get the current board state
  public getBoardState(): BoardState {
    return this.boardState;
  }

  // Get the current turn
  public getCurrentTurn(): 'Player' | 'Computer' {
    return this.currentTurn;
  }

  // Get the captured pieces of the player
  public getCapturedPlayerPieces(): Piece[] {
    return this.capturedPlayerPieces;
  }

  // Check if the game is over
  public isGameOver(): boolean {
    return this.gameOver;
  }

  // Get the captured pieces of the computer
  public getCapturedComputerPieces(): Piece[] {
    return this.capturedComputerPieces;
  }

  // Play the capture sound
  private playCaptureSound() {
    const captureSound = new Audio(this.currentTurn === 'Player' ? '/sounds/player-capture.wav' : '/sounds/computer-capture.wav');
    captureSound.play();
  }

  // Play the beep sound
  private playBeepSound() {
    const beep = new Audio('/sounds/chess-move-click.wav');
    beep.play();
  }

  // Update the list of captured pieces
  private updateCapturedPieces(piece: Piece) {
    if (this.currentTurn === 'Player') {
      this.capturedComputerPieces.push(piece);
    } else {
      this.capturedPlayerPieces.push(piece);
    }
  }

  // Show an alert message
  private showAlert(message: string) {
    logger.debug(message);
    this.setAlertMessageCallback(message); // Notify the App component
    this.setAlertVisibleCallback(true); // Set alert visibility to true
  }

  // Switch the turn between player and computer
  switchTurn() {
    if (this.gameOver) {
      return;
    }
    this.currentTurn = this.currentTurn === 'Player' ? 'Computer' : 'Player';
    this.turnCount++;
    logger.debug("switchTurn() - Switched turn to: " + this.currentTurn);
  }

  public getTurnCount(): number {
    return this.turnCount;
  }
}

export default Game;
